import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog'
import { MatCard, MatCardHeader, MatCardTitle } from '@angular/material/card'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatDivider } from '@angular/material/divider'
import { CurrencyInputDirective } from '../../../shared/directives/currencyInput'
import { convertCurrency } from '../../../shared/utils/utils'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { DialogConfirmComponent } from '../../../shared/components/dialog-confirm/dialog-confirm.component'
import { SupplierPortalQuoteService } from '../../../core/services/supplier-portal/supplier-portal-quote.service'
import {
  ErrorRequestModel,
  ResponseRequestModel,
} from '../../../core/models/response.model'
import {
  FornecedorCotacaoPortalVm,
  FornecedorCotacaoValorEnvio,
} from '../../../core/models/supplier-portal.model'
import { labelStatus, podeResponder, STATUS_ITEM } from '../supplier-portal-quote-status.util'
import { ModalSupplierPortalQuoteItineraryComponent } from '../modal-supplier-portal-quote-itinerary/modal-supplier-portal-quote-itinerary.component'

// Converte o texto da máscara de moeda ("1.234,56") em número. Campo em branco vale 0:
// convertCurrency('') é parseFloat('') = NaN, que serializa como null e quebra o decimal
// não-anulável do backend. O caso numérico é tratado antes de chamar convertCurrency porque
// ela é bidirecional — com número na entrada devolve string, e o valor se perderia aqui.
function toNumber(value: any): number {
  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value
  }
  const parsed = convertCurrency(value ?? '')
  return typeof parsed === 'number' && !isNaN(parsed) ? parsed : 0
}

// O backend exige custo > 0. Validators.required sozinho aceita '0,00', que a API recusa com 400.
function custoMaiorQueZero(control: AbstractControl): ValidationErrors | null {
  return toNumber(control.value) > 0 ? null : { custoInvalido: true }
}

// Modal de precificação do Portal do Fornecedor. Espelha o "Valores cotação" do operador, com
// três diferenças: só custo é obrigatório (há trecho sem hora extra nem km extra), o itinerário
// vem embutido — é aqui que o fornecedor precisa dele, na hora de precificar — e o envio é
// definitivo, por isso a confirmação antes de gravar.
@Component({
  selector: 'modal-supplier-portal-quote-price',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinner,
    MatDivider,
    CurrencyInputDirective,
  ],
  templateUrl: './modal-supplier-portal-quote-price.component.html',
  styleUrl: './modal-supplier-portal-quote-price.component.scss',
})
export class ModalSupplierPortalQuotePriceComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ModalSupplierPortalQuotePriceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FornecedorCotacaoPortalVm,
    private dialog: MatDialog,
    private snackbar: SnackbarCustomService,
    private supplierPortalQuoteService: SupplierPortalQuoteService,
  ) {
    this.item = data
  }

  item: FornecedorCotacaoPortalVm
  form: FormGroup = new FormGroup({})
  loading = false

  @Output() response = new EventEmitter<boolean>()

  readonly statusRecusado = STATUS_ITEM.RECUSADO_FORNECEDOR

  // Trecho já respondido (ou envio encerrado) abre em modo leitura.
  get editavel(): boolean {
    return podeResponder(this.item)
  }

  // Rótulo do portal ("Aceita"/"Encerrada"), não a descrição crua do banco.
  get descricaoStatus(): string {
    return labelStatus(this.item)
  }

  ngOnInit(): void {
    this.createForm()
  }

  close() {
    this.dialogRef.close()
  }

  viewItinerariesItem() {
    this.dialog.open(ModalSupplierPortalQuoteItineraryComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '1000px',
      maxWidth: '1000px',
      maxHeight: '620px',
      data: { itemCotacaoId: this.item.itemCotacaoId },
    })
  }

  // O contexto do trecho é texto no cabeçalho (mesmo formato da tela de cotação do operador),
  // não campo de formulário — aqui só entram os três custos.
  createForm(): void {
    this.form = this.formBuilder.group({
      cost: ['', [Validators.required, custoMaiorQueZero]],
      costOvertime: ['', Validators.required],
      costKmExtra: ['', Validators.required],
      // Opcional — o backend limita a 500 e a grava como primeira observação do trecho.
      observacao: ['', Validators.maxLength(500)],
    })

    if (!this.editavel) {
      this.form.get('cost')?.setValue(this.formatValor(this.item.custo))
      this.form.get('costOvertime')?.setValue(this.formatValor(this.item.custoHoraExtra))
      this.form.get('costKmExtra')?.setValue(this.formatValor(this.item.custoKmExtra))
      this.form.get('cost')?.disable()
      this.form.get('costOvertime')?.disable()
      this.form.get('costKmExtra')?.disable()
      this.form.get('observacao')?.disable()
    }
  }

  private formatValor(valor: number): string {
    return valor ? (convertCurrency(valor) as string) : ''
  }

  // O fornecedor responde uma vez só — confirmar repetindo os valores antes de gravar.
  confirmarEnvio() {
    const model = this.createModel()
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '460px',
      width: '460px',
      data: {
        title: 'Confirmar valores',
        message:
          `Custo ${this.exibirValor(model.custo)}, hora extra ${this.exibirValor(model.custoHoraExtra)}, ` +
          `km extra ${this.exibirValor(model.custoKmExtra)}. ` +
          'Depois de enviar não é possível alterar. Deseja continuar?',
        confirmLabel: 'Enviar valores',
      },
    })
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.registrarValor(model)
      }
    })
  }

  private exibirValor(valor: number): string {
    return `R$ ${convertCurrency(valor)}`
  }

  private registrarValor(model: FornecedorCotacaoValorEnvio) {
    this.loading = true
    this.supplierPortalQuoteService
      .registrarValor(this.item.itemCotacaoId, model)
      .subscribe({
        next: (response: ResponseRequestModel) => {
          this.snackbar.success(response.message ?? 'Valores enviados com sucesso!')
          this.response.emit(true)
          this.close()
        },
        error: (err: ErrorRequestModel) => {
          this.loading = false
          this.snackbar.errorRequest(err)
        },
        complete: () => {
          this.loading = false
        },
      })
  }

  createModel(): FornecedorCotacaoValorEnvio {
    const model = new FornecedorCotacaoValorEnvio()
    model.custo = toNumber(this.form.get('cost')?.value)
    model.custoHoraExtra = toNumber(this.form.get('costOvertime')?.value)
    model.custoKmExtra = toNumber(this.form.get('costKmExtra')?.value)
    // Em branco vai como null, não como string vazia — o backend só cria a observação quando vem texto.
    const observacao = (this.form.get('observacao')?.value ?? '').trim()
    model.observacao = observacao || null
    return model
  }
}
